/**
 * CreativeViewerModal - Модальное окно для просмотра креатива
 * =============================================================
 * 
 * Полностью переписано по образцу EnhancedImageLightbox из OpenAIImage
 */

import React, { useState, useMemo, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { MetricsService } from '../../services/metricsService';
import { videoCacheService } from '../../services/videoCacheService';
import { 
  X, 
  Download, 
  ExternalLink,
  Video as VideoIcon,
  Image as ImageIcon
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card } from '../ui/card';
import { ScrollArea } from '../ui/scroll-area';

/**
 * Модальное окно с плеером и статистикой
 */
export function CreativeViewerModal({ creative, allVariants, open, onClose }) {
  const [videoUrl, setVideoUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Агрегированные данные для группы креативов
  const aggregatedData = useMemo(() => {
    if (!allVariants || allVariants.length === 0) return null;
    
    const totals = allVariants.reduce((acc, variant) => ({
      installs: acc.installs + variant.installs,
      cost: acc.cost + variant.cost,
      revenue: acc.revenue + (variant.revenue?.inapps || 0) + (variant.revenue?.admon || 0),
      campaigns: [...acc.campaigns, variant.campaign]
    }), { installs: 0, cost: 0, revenue: 0, campaigns: [] });
    
    const avgRoas = totals.cost > 0 ? (totals.revenue / totals.cost) * 100 : 0;
    const uniqueCampaigns = [...new Set(totals.campaigns)];
    
    return {
      ...totals,
      avgRoas,
      uniqueCampaigns,
      campaignCount: uniqueCampaigns.length
    };
  }, [allVariants]);
  
  // Загрузка видео при открытии модалки
  useEffect(() => {
    if (!creative || !creative.preview || !open) {
      return;
    }
    
    const fileId = creative.preview.webViewLink?.match(/\/d\/([^/]+)/)?.[1];
    if (!fileId) {
      setError('Invalid file ID');
      return;
    }
    
    // URL держим в локальной переменной эффекта, а не читаем из state в cleanup:
    // замыкание видит значение на момент запуска эффекта (null при первом открытии),
    // поэтому revokeObjectURL по state не срабатывал никогда — blob утекал.
    let objectUrl = null;
    let cancelled = false;

    loadFullVideo(fileId).then((url) => {
      if (cancelled) {
        if (url && url.startsWith('blob:')) URL.revokeObjectURL(url);
      } else {
        objectUrl = url;
      }
    });

    return () => {
      cancelled = true;
      if (objectUrl && objectUrl.startsWith('blob:')) {
        URL.revokeObjectURL(objectUrl);
      }
      // Иначе при повторном открытии в <video> попадёт уже отозванный URL
      setVideoUrl(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creative, open]);
  
  const loadFullVideo = async (fileId) => {
    try {
      setIsLoading(true);
      setError(null);
      
      const url = `/api/drive/file/${fileId}`;
      
      // Проверяем кеш
      const cachedBlob = await videoCacheService.get(url);
      if (cachedBlob) {
        const blobUrl = URL.createObjectURL(cachedBlob);
        setVideoUrl(blobUrl);
        setIsLoading(false);
        return blobUrl;
      }
      
      // Загружаем с сервера
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const blob = await response.blob();
      await videoCacheService.set(url, blob);
      
      const blobUrl = URL.createObjectURL(blob);
      setVideoUrl(blobUrl);
      setIsLoading(false);
      return blobUrl;
      
    } catch (err) {
      console.error('[CreativeViewerModal] Ошибка загрузки:', err);
      setError(err.message);
      setIsLoading(false);
      // Fallback на прямую ссылку
      const fallbackUrl = `/api/drive/file/${fileId}`;
      setVideoUrl(fallbackUrl);
      return fallbackUrl;
    }
  };
  
  // Обработка клавиш
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    
    if (open) {
      window.addEventListener('keydown', handleKeyDown);
    }
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, onClose]);
  
  // Загрузка контента
  const handleDownload = async () => {
    try {
      if (!videoUrl) return;
      
      const filename = `${creative.adName || 'video'}.mp4`;
      
      const response = await fetch(videoUrl);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      link.style.display = 'none';
      
      document.body.appendChild(link);
      link.click();
      
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      }, 100);
      
    } catch (err) {
      console.error('Ошибка при скачивании:', err);
      window.open(videoUrl, '_blank');
    }
  };
  
  if (!open || !creative || !creative.preview) return null;
  
  const isVideo = creative.preview.mimeType?.startsWith('video/');
  
  // Рендерим через портал в body (как в OpenAIImage)
  return ReactDOM.createPortal(
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 z-[9999] bg-black/80 animate-in fade-in-0"
        onClick={onClose}
      />
      
      {/* Content */}
      <div className="fixed left-[50%] top-[50%] z-[9999] w-[95%] max-w-[1600px] max-h-[95vh] translate-x-[-50%] translate-y-[-50%] p-0 gap-0 bg-background border border-border rounded-lg shadow-lg animate-in fade-in-0 zoom-in-95">
        {/* Header */}
        <div className="px-6 py-4 border-b border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              {isVideo ? <VideoIcon className="h-5 w-5 flex-shrink-0" /> : <ImageIcon className="h-5 w-5 flex-shrink-0" />}
              <h2 className="text-lg font-semibold truncate">{creative.adName}</h2>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="h-8 w-8 flex-shrink-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
        
        <div className="flex gap-4 p-6 h-[calc(95vh-80px)]">
          {/* Основная область с видео - 70% */}
          <div className="flex-1 relative bg-black/20 rounded-lg group min-h-0 flex items-center justify-center">
            {isLoading ? (
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent mx-auto mb-4" />
                <p className="text-white">Загрузка видео...</p>
              </div>
            ) : error ? (
              <div className="text-center text-white">
                <p className="mb-2">Ошибка загрузки</p>
                <p className="text-sm text-gray-400">{error}</p>
              </div>
            ) : videoUrl ? (
              <div className="relative max-w-full max-h-full flex items-center justify-center">
                <video 
                  className="block max-w-full max-h-[calc(95vh-200px)] object-contain rounded"
                  controls
                  autoPlay
                  loop
                  preload="auto"
                  playsInline
                >
                  <source src={videoUrl} type="video/mp4" />
                  Ваш браузер не поддерживает видео
                </video>
              </div>
            ) : (
              <div className="text-white">No video available</div>
            )}
          </div>
          
          {/* Боковая панель - увеличена в 2 раза */}
          <ScrollArea className="w-[640px] min-w-[640px] h-full">
            <div className="space-y-4">
              {/* Статистика */}
              <Card className="p-4">
                <h4 className="text-sm font-semibold mb-3">Статистика</h4>
                
                <div className="space-y-2">
                  {aggregatedData ? (
                    // Агрегированные данные для группы
                    <>
                      <div className="flex justify-between items-center py-1">
                        <span className="text-sm text-muted-foreground">Campaigns:</span>
                        <Badge variant="default">{aggregatedData.campaignCount}</Badge>
                      </div>
                      
                      <div className="flex justify-between items-center py-1">
                        <span className="text-sm text-muted-foreground">Platform:</span>
                        <Badge variant="secondary">{creative.platform}</Badge>
                      </div>
                      
                      <div className="flex justify-between items-center py-1 pt-3 mt-3 border-t">
                        <span className="text-sm text-muted-foreground">Total Installs:</span>
                        <Badge variant="secondary">{MetricsService.formatNumber(aggregatedData.installs)}</Badge>
                      </div>
                      
                      <div className="flex justify-between items-center py-1">
                        <span className="text-sm text-muted-foreground">Avg ROAS:</span>
                        <Badge variant={aggregatedData.avgRoas >= 100 ? 'default' : 'secondary'}>
                          {aggregatedData.avgRoas.toFixed(1)}%
                        </Badge>
                      </div>
                      
                      <div className="flex justify-between items-center py-1">
                        <span className="text-sm text-muted-foreground">Total Cost:</span>
                        <Badge variant="secondary">{MetricsService.formatCurrency(aggregatedData.cost)}</Badge>
                      </div>
                      
                      <div className="flex justify-between items-center py-1">
                        <span className="text-sm text-muted-foreground">Total Revenue:</span>
                        <Badge variant="secondary">{MetricsService.formatCurrency(aggregatedData.revenue)}</Badge>
                      </div>
                      
                      <div className="pt-3 mt-3 border-t">
                        <p className="text-xs text-muted-foreground mb-2">Used in campaigns:</p>
                        <div className="flex flex-wrap gap-1">
                          {aggregatedData.uniqueCampaigns.map((campaign, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs">
                              {campaign}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </>
                  ) : (
                    // Данные для одного креатива
                    <>
                      <div className="flex justify-between items-center py-1">
                        <span className="text-sm text-muted-foreground">Campaign:</span>
                        <Badge variant="secondary" className="text-xs max-w-[200px] truncate">
                          {creative.campaign}
                        </Badge>
                      </div>
                      
                      <div className="flex justify-between items-center py-1">
                        <span className="text-sm text-muted-foreground">Platform:</span>
                        <Badge variant="secondary">{creative.platform}</Badge>
                      </div>
                      
                      <div className="flex justify-between items-center py-1 pt-3 mt-3 border-t">
                        <span className="text-sm text-muted-foreground">Installs:</span>
                        <Badge variant="secondary">{MetricsService.formatNumber(creative.installs)}</Badge>
                      </div>
                      
                      <div className="flex justify-between items-center py-1">
                        <span className="text-sm text-muted-foreground">ROAS:</span>
                        <Badge variant={creative.roas >= 100 ? 'default' : 'secondary'}>
                          {creative.roas?.toFixed(1)}%
                        </Badge>
                      </div>
                      
                      <div className="flex justify-between items-center py-1">
                        <span className="text-sm text-muted-foreground">Cost:</span>
                        <Badge variant="secondary">{MetricsService.formatCurrency(creative.cost)}</Badge>
                      </div>
                      
                      <div className="flex justify-between items-center py-1">
                        <span className="text-sm text-muted-foreground">Inapps:</span>
                        <Badge variant="secondary">{MetricsService.formatCurrency(creative.revenue?.inapps || 0)}</Badge>
                      </div>
                      
                      <div className="flex justify-between items-center py-1">
                        <span className="text-sm text-muted-foreground">AdMon:</span>
                        <Badge variant="secondary">{MetricsService.formatCurrency(creative.revenue?.admon || 0)}</Badge>
                      </div>
                      
                      <div className="flex justify-between items-center py-1">
                        <span className="text-sm text-muted-foreground">Total Revenue:</span>
                        <Badge variant="secondary">
                          {MetricsService.formatCurrency((creative.revenue?.inapps || 0) + (creative.revenue?.admon || 0))}
                        </Badge>
                      </div>
                    </>
                  )}
                </div>
              </Card>
              
              {/* Действия */}
              <div className="space-y-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="w-full"
                  onClick={handleDownload}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Скачать видео
                </Button>
                
                {creative.preview.webViewLink && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="w-full"
                    onClick={() => window.open(creative.preview.webViewLink, '_blank')}
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Open in Google Drive
                  </Button>
                )}
              </div>
            </div>
          </ScrollArea>
        </div>
      </div>
    </>,
    document.body
  );
}
